import { clean } from "~/utilities/helpers";
import { has, isEmpty, reduce } from "lodash";

const createProductSeoSchema = (products, siteUrl, logo) => {
    const itemListElement = products.map((product, index) => {
        const organization = `
            {
                "@type": "Organization",
                "url": "${siteUrl}/",
                "logo": "${logo}"
            }
        `;
        const productDescription =
            product.productInformation.productDescription;
        const productUrl = `${siteUrl}/${product.slug}/`;
        const contactUrl = `${siteUrl}/contact/`;

        return `
            {
                "@type": "ListItem",
                "position": "${index + 1}",
                "item": {
                    "@type": "Product",
                    "image": "${product?.thumbnail?.node?.sourceUrl ?? ""}",
                    "url": "${productUrl}",
                    "name": "${product.title}",
                    "brand": ${organization},
                    "sku": "${product.id}",
                    "description": "${
                        productDescription
                            ? clean(productDescription)
                            : product.title
                    }",
                    "offers": {
                        "@type": "Offer",
                        "availability": "https://schema.org/InStock",
                        "offeredBy": ${organization},
                        "priceCurrency": "USD",
                        "url": "${contactUrl}"
                    }
                }
            }
        `;
    });

    return `
            {
                "@context": "https://schema.org/",
                "@type": "ItemList",
                "url": "${siteUrl}/",
                "numberOfItems": "${products.length}",
                "itemListElement": [${itemListElement}]
            }
    `;
};

const createFAQSeoSchema = (faqs) => {
    if (isEmpty(faqs)) {
        return "";
    }

    const mainEntity = faqs.map((faq) => {
        return `
            {
                "@type": "Question",
                "name": "${faq.title}",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "${clean(faq.content)}"
                }
            }
        `;
    });

    return `
        {
            "@context": "https://schema.org/",
            "@type": "FAQPage",
            "mainEntity": [${mainEntity}]
        }
    `;
};

const createQuoteSchema = (quote) => {
    if (isEmpty(quote)) {
        return "";
    }

    return `{
                "@context": "https://schema.org/",
                "@type": "Quotation",
                "creator": {
                    "@type": "Person",
                    "name": "${quote.attribution}"
                },
                "text": "${quote.quote}"
            }`;
};

export const createSeoSchemas = ({ schemas, seo }) => {
    const seoSchemas = {
        faqsBlock: (block) => createFAQSeoSchema(block.faqsBlock.faqs),
        quoteBlock: (block) => createQuoteSchema(block.quoteBlock),
        applicationQuote: (schema) =>
            createQuoteSchema(schema.applicationQuote),
        productCategoryFaqs: (schema) =>
            createFAQSeoSchema(schema.productCategoryFaqs),
        productCategoryProducts: (schema, siteUrl, logo) =>
            createProductSeoSchema(
                schema.productCategoryProducts,
                siteUrl,
                logo,
            ),
    };

    return reduce(
        seoSchemas,
        (acc, callback, key) => {
            const seoSchema = schemas.find((schema) => has(schema, key));
            if (seoSchema) {
                const args = [seoSchema];

                if (seo?.siteUrl) {
                    args.push(seo.siteUrl);
                }

                if (seo?.logo) {
                    args.push(seo.logo);
                }

                return [...acc, callback(...args)];
            }
            return acc;
        },
        [],
    );
};
